import React, { useState, useRef } from "react";
import { Select, DatePicker, Dropdown, notification } from "antd";
import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import GenericTable from "../GenericTable";
import UploadIcon from "../../../Assets/Icons/upload.svg";
import "./style.css";
import docIcon from "../../../Assets/Icons/document.svg";
import pdfIcon from "../../../Assets/Icons/pdf.svg";
import GenericModal from "../../Modals/GenericModal";

const { Option } = Select;

const CATEGORY_COLORS = [
  { bg: "var(--Light-Primary)", color: "var(--Primary)" },
  { bg: "var(--light-warning)", color: "var(--warning)" },
  { bg: "var(--light-success)", color: "var(--success)" },
  { bg: "#e0f2fe", color: "#0369a1" },
  { bg: "#f0fdf4", color: "#15803d" },
  { bg: "#fef9c3", color: "#a16207" },
  { bg: "#fce7f3", color: "#be185d" },
  { bg: "#ede9fe", color: "#6d28d9" },
];

const KNOWN_CATEGORY_COLORS = {
  registration: { bg: "var(--Light-Primary)", color: "var(--Primary)" },
  fitness_certificate: { bg: "var(--light-warning)", color: "var(--warning)" },
  insurance_certificate: { bg: "var(--light-success)", color: "var(--success)" },
  license: { bg: "#e0f2fe", color: "#0369a1" },
  id_card: { bg: "#fce7f3", color: "#be185d" },
  proof_of_address: { bg: "#ede9fe", color: "#6d28d9" },
};

const getCategoryColor = (category) => {
  const str = category || "unknown";
  const known = KNOWN_CATEGORY_COLORS[str.toLowerCase().replace(/\s+/g, "_")];
  if (known) return known;

  const hash = str.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return CATEGORY_COLORS[hash % CATEGORY_COLORS.length];
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const formatCategoryLabel = (str) =>
  str && str.includes("_")
    ? str
        .split("_")
        .map((word) => capitalize(word))
        .join(" ")
    : capitalize(str);

const GenericDocumentManagerTable = ({
  initialDocuments = [],
  setDocuments,
  showTableOnly = false,
  showUploadOnly = false,
  customTitle = "Documents",
  hideRemoveAction = false,
  hideDateColumn = false,
  documentTypes = [],
  disabled = false,
  onDeleteDocument,
}) => {
  const fileInputRef = useRef(null);
  const [docType, setDocType] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleConfirmDelete = async () => {
    if (!selectedRow) return;

    if (onDeleteDocument) {
      try {
        await onDeleteDocument(selectedRow);
      } catch (err) {
        notification.error({
          message: "Delete Failed",
          description: err?.message || "Failed to delete document.",
        });
        setIsDeleteModalOpen(false);
        return;
      }
    }

    setDocuments((prev) =>
      prev.filter((d) => (d.id ?? d.key) !== (selectedRow.id ?? selectedRow.key)),
    );
    setIsDeleteModalOpen(false);

    notification.success({
      message: "Document Removed",
      description: `${selectedRow?.name} has been successfully deleted.`,
      placement: "topRight",
    });
  };

  const documents = initialDocuments;

  const shouldShowUpload = !showTableOnly;

  const selectedType = documentTypes.find(
    (type) => (typeof type === "string" ? type : type.value) === docType,
  );

  const docTypeLabel = !selectedType
    ? docType
    : typeof selectedType === "string"
      ? selectedType
      : selectedType.label;

  const allowedFormats =
    selectedType && typeof selectedType === "object"
      ? selectedType.allowedFormats
      : null;

  const acceptAttr =
    allowedFormats && allowedFormats.length
      ? allowedFormats.map((ext) => `.${ext.replace(/^\./, "")}`).join(",")
      : ".doc,.docx,.pdf";

  const openFileBrowser = () => {
    if (disabled) return;
    fileInputRef.current.click();
  };

  const handleExpiryChange = (value) => {
    if (value && !value.isAfter(new Date(), "day")) {
      notification.error({
        message: "Invalid Expiry Date",
        description: "Expiry date must be a future date",
      });
      setExpiryDate(null);
      return;
    }
    setExpiryDate(value);
  };

  const handleSelectChange = (val) => {
    setDocType(val);
  };

  const handleUpload = (file, index) => {
    if (!docType) {
      notification.error({
        message: "Select Document Type",
        description: "Please select a document type first",
      });
      return null;
    }

    if (allowedFormats && allowedFormats.length) {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (!allowedFormats.map((f) => f.toLowerCase()).includes(ext)) {
        notification.error({
          message: "Unsupported File Format",
          description: `${docTypeLabel} only accepts: ${allowedFormats
            .join(", ")
            .toUpperCase()}`,
        });
        return null;
      }
    }

    const fileUrl = URL.createObjectURL(file);
    return {
      key: `${Date.now()}-${index}`,
      name: file.name,
      category: docType,
      date: new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      expiryDate: expiryDate ? expiryDate.format("YYYY-MM-DD") : null,
      status: "Pending",
      fileUrl,
      file,
    };
  };

  const processFiles = (fileList) => {
    const files = Array.from(fileList);
    const newDocs = files
      .map((file, index) => handleUpload(file, index))
      .filter((doc) => doc !== null);

    if (newDocs.length > 0) {
      setDocuments((prev) => [...newDocs, ...prev]);

      const fileNames = newDocs.map((d) => d.name).join(", ");
      const isMultiple = newDocs.length > 1;

      notification.success({
        message: "Upload Successful",

        description: `${fileNames} ${isMultiple ? "have" : "has"} been uploaded successfully.`,
        placement: "topRight",
      });
    }
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    processFiles(e.dataTransfer.files);
  };

  const categoryFilters = Array.from(
    new Set(documents.map((doc) => doc.category)),
  ).map((cat) => ({
    text: cat,
    value: cat,
  }));

  const toCategoryKey = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getAllowedFormats = (category) => {
    const key = toCategoryKey(category);
    const match = documentTypes.find(
      (type) =>
        toCategoryKey(typeof type === "string" ? type : type.value) === key,
    );
    return match && typeof match === "object" ? match.allowedFormats : null;
  };

  const getRowFormats = (record) =>
    record.allowedFormats && record.allowedFormats.length
      ? record.allowedFormats
      : getAllowedFormats(record.category) || [];

  const formatFilters = Array.from(
    new Set(
      documents.flatMap((doc) =>
        getRowFormats(doc).map((format) => format.toLowerCase()),
      ),
    ),
  ).map((format) => ({
    text: format.toUpperCase(),
    value: format,
  }));

  const columns = [
    {
      title: "Document Name",
      dataIndex: "name",
      render: (name) => {
        const icon = (name || "").toLowerCase().endsWith(".pdf")
          ? pdfIcon
          : docIcon;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={icon} alt="doc" style={{ width: 20 }} />
            {name}
          </div>
        );
      },
      enableSearch: true,
    },
    {
      title: "Category",
      dataIndex: "category",
      filters: categoryFilters,
      onFilter: (value, record) => record.category === value,
      render: (category) => {
        const safeCategory = category || "unknown";
        const { bg, color } = getCategoryColor(safeCategory);

        return (
          <span
            className="badge-category"
            style={{ backgroundColor: bg, color }}
          >
            {formatCategoryLabel(safeCategory)}
          </span>
        );
      },
    },
    {
      title: "Allowed Formats",
      dataIndex: "category",
      key: "allowedFormats",
      filters: formatFilters,
      onFilter: (value, record) =>
        getRowFormats(record)
          .map((format) => format.toLowerCase())
          .includes(value),
      render: (category, record) => {
        const formats = getRowFormats(record);
        if (!formats.length) return "-";

        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {formats.map((format) => (
              <span key={format} className="format-badge">
                {format.toUpperCase()}
              </span>
            ))}
          </div>
        );
      },
    },
    ...(hideDateColumn
      ? []
      : [
          {
            title: "Date",
            dataIndex: "date",
            sorter: (a, b) =>
              (new Date(a.date).getTime() || 0) -
              (new Date(b.date).getTime() || 0),
          },
        ]),
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      sorter: (a, b) =>
        (new Date(a.expiryDate).getTime() || 0) -
        (new Date(b.expiryDate).getTime() || 0),
      render: (expiryDate) => expiryDate || "-",
    },
    {
      title: "Actions",
      render: (_, record) => {
        if (hideRemoveAction || disabled) {
          return (
            <EyeOutlined
              className="action-btn"
              style={{ cursor: "pointer", fontSize: 18, color: "#4F46E5" }}
              onClick={() => window.open(record.fileUrl, "_blank")}
            />
          );
        }

        const items = [
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            onClick: () => window.open(record.fileUrl, "_blank"),
          },
          {
            key: "delete",
            label: "Remove",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
              setSelectedRow(record);
              setIsDeleteModalOpen(true);
            },
          },
        ];

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <BsThreeDotsVertical
              className="action-btn"
              style={{ cursor: "pointer", color: "#4F46E5" }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="document-manager">
      {shouldShowUpload && !disabled && (
        <>
          <div className="upload-header">
            <div className="upload-header-title">
              <div className="upload-header-icon">
                <img src={UploadIcon} alt="upload icon" width="100%" />
              </div>
              <h3>Upload Documents</h3>
            </div>

            <Select
              placeholder="Select Document Type"
              className="doc-select"
              style={{ width: 200 }}
              onChange={handleSelectChange}
              value={docType}
            >
              {documentTypes.map((type) => {
                const value = typeof type === "string" ? type : type.value;
                const label = typeof type === "string" ? type : type.label;
                return (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                );
              })}
            </Select>

            {docType && (
              <DatePicker
                placeholder="Expiry Date"
                className="doc-expiry-picker"
                style={{ width: 160 }}
                value={expiryDate}
                onChange={handleExpiryChange}
              />
            )}
          </div>

          <div
            className="upload-dropzone"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <div className="upload-icon">
              <img src={UploadIcon} alt="upload" width={32} />
            </div>
            <p className="upload-text">Choose a file or drag & drop it here</p>
            <p className="upload-subtext">
              Current Selected format: <b>{docTypeLabel || "None"}</b>
              {allowedFormats && allowedFormats.length
                ? ` (Accepted: ${allowedFormats.join(", ").toUpperCase()})`
                : ""}
              , up to 50MB
            </p>
            <button className="browse-btn" onClick={openFileBrowser}>
              Browse File
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept={acceptAttr}
              multiple
            />
          </div>
        </>
      )}

      <GenericTable
        title={customTitle}
        dataSource={documents}
        columns={columns}
      />

      <GenericModal
        open={isDeleteModalOpen}
        title="Remove Document"
        subtitle={`Are you sure you want to delete ${selectedRow?.name}?`}
        highlightText={selectedRow?.name}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Remove Document"
        cancelText="Cancel"
        showReason={false}
      />
    </div>
  );
};

export default GenericDocumentManagerTable;
